import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { notify } from '../utils/toast';
import { getNFDomain } from '../utils/nfd';
import { getWalletInfo } from '../utils/wallet';
import { checkSubscription, makePaymentTransaction, verifyProfile } from '../services/api/api';
import { storage } from '../lib/storage';
import { useWallet, WalletId } from '@txnlab/use-wallet-react';
// import { setupAlgodClient, walletManager } from '../services/wallet/walletConnection';
import type { WalletType } from '../services/wallet/walletConnection';

// Extended wallet metadata type that includes description
interface ExtendedWalletMetadata {
  name: string;
  icon?: string;
  description?: string;
}
import { saveWalletInfo, clearWalletInfo } from '../services/wallet/walletStorage';

// Helper function to convert null to undefined for rekeyTo
const nullToUndefined = (value: string | null): string | undefined => {
  return value === null ? undefined : value;
};

export const useWalletConnect = () => {
  const navigate = useNavigate();
  const [connecting, setConnecting] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [balance, setBalance] = useState<number>();
  const [minBalance, setMinBalance] = useState<number>();

  // Use the useWallet hook from @txnlab/use-wallet-react
  const {
    activeAddress,
    activeAccount,
    signTransactions,
    transactionSigner,
    algodClient,
    wallets
  } = useWallet();

  // Derive active wallet and accounts from useWallet
  const activeWallet = activeAccount?.name?.toLowerCase() || null;
  const accounts = activeAddress ? [activeAddress] : [];

  // Check for active accounts when component mounts or when activeAddress changes
  useEffect(() => {
    const checkActiveAccounts = async () => {
      try {
        if (activeAddress) {
          const info = await getWalletInfo(activeAddress);

          saveWalletInfo(activeAddress, info.balance, info.minBalance, nullToUndefined(info.rekeyTo));

          setBalance(info.balance);
          setMinBalance(info.minBalance);

          localStorage.setItem('walletAddress', activeAddress);
          // Store the wallet type if we have it
          if (activeWallet) {
            localStorage.setItem('walletType', activeWallet);
          }

          const nfd = await getNFDomain(activeAddress);
          if (nfd) {
            localStorage.setItem('nfdName', nfd);
          }
        }
      } catch (error) {
        console.error('Error checking active accounts:', error);
      }
    };

    checkActiveAccounts();
  }, [activeAddress, activeWallet]);

  const checkLocalProfile = async (walletAddress: string) => {
    const profile = await storage.getProfile(walletAddress);
    const token = localStorage.getItem('goplausible_token');
    return !!(profile && token);
  };

  const handleSubscription = async (
    walletAddress: string,
    walletAddressRekeyTo: string | null | undefined,
    algodClient: any
  ) => {
    try {
      if (await checkLocalProfile(walletAddress)) {
        const isValid = await verifyProfile(walletAddress);
        if (isValid) {
          notify.success('Profile verified');
          return true;
        }
      }

      // Use the transactionSigner from useWallet
      const txnBytes = await makePaymentTransaction(0.001, walletAddress, algodClient, transactionSigner);
      if (!txnBytes) {
        throw new Error('Failed to create subscription payment');
      }

      const params = await algodClient.getTransactionParams().do();
      const result = await checkSubscription(walletAddress, txnBytes, params, walletAddressRekeyTo);

      if (!result) {
        throw new Error('Failed to verify subscription');
      }

      await storage.setProfile(walletAddress, result.profile);
      return true;
    } catch (error: any) {
      console.error('Subscription error:', error);
      notify.error(error?.message || 'Failed to verify subscription');
      return false;
    }
  };
  

  const handleWalletConnect = useCallback(async (walletType: WalletType) => {
    if (connecting) return;
    setConnecting(true);

    try {
      // Find the wallet by ID - handle case insensitively
      // Convert walletType to the format expected by WalletId
      let walletIdKey: string;

      // Handle special case for 'pera' which should map to WalletId.PERA
      if (walletType.toLowerCase() === 'pera') {
        walletIdKey = 'PERA';
      } else {
        walletIdKey = walletType.toUpperCase();
      }

      // Find the wallet in the available wallets
      const wallet = wallets.find(w => w.id === walletIdKey || w.id === WalletId[walletIdKey as keyof typeof WalletId]);

      if (!wallet) {
        console.error(`Available wallets:`, wallets.map(w => w.id));
        throw new Error(`Wallet provider ${walletType} not found`);
      }

      // Check if the wallet is already connected
      if (wallet.isConnected) {
        console.log(`Wallet ${walletType} is already connected`);

        // If we already have an active account, just proceed with authentication
        if (activeAddress && activeAddress === wallet.accounts[0]?.address) {
          notify.success(`Already connected to ${walletType} wallet`);
          return;
        }

        // Otherwise, disconnect first and then reconnect
        try {
          await wallet.disconnect();
          console.log(`Disconnected from ${walletType} wallet`);
        } catch (disconnectError) {
          console.error(`Error disconnecting from ${walletType} wallet:`, disconnectError);
          // Continue with connect attempt even if disconnect fails
        }
      }

      // Connect to the wallet using the wallet's connect method
      await wallet.connect();

      notify.success(`Connected to ${walletType} wallet`);
    } catch (error: any) {
      if (error?.data?.type !== "CONNECT_MODAL_CLOSED") {
        console.error('Wallet connection error:', error);
        notify.error(error?.message || 'Failed to connect wallet');
      }
    } finally {
      setConnecting(false);
    }
  }, [connecting, wallets, activeAddress]);

  const authenticateWallet = useCallback(async (walletAddress: string) => {
    if (authenticating || !walletAddress) return;
    setAuthenticating(true);

    const isExtension = import.meta.env.VITE_EXTENSION_BUILD === 'true' || window.location.protocol === 'chrome-extension:';
    const goToChat = () => {
      if (isExtension) {
        window.location.hash = '#/convo';
      } else {
        navigate('/convo', { replace: true });
      }
    };

    try {
      // Use algodClient from useWallet
      console.log('Starting authentication process for wallet:', walletAddress);
      console.log('Transaction signer available:', !!transactionSigner);
      console.log('Algod client available:', !!algodClient);

      if (!activeAccount) {
        console.error('No active account found');
        throw new Error('Wallet not properly connected');
      }

      // For debugging
      console.log('Authenticating wallet:', walletAddress);
      console.log('Active account:', activeAccount);
      console.log('Using algodClient:', algodClient);

      // First check if we already have a valid profile
      if (await checkLocalProfile(walletAddress)) {
        const isValid = await verifyProfile(walletAddress);
        if (isValid) {
          // Get the existing profile to ensure we don't lose any data
          const existingProfile = await storage.getProfile(walletAddress);
          if (existingProfile) {
            // Update only necessary fields but keep the rest of the profile intact
            await storage.setProfile(walletAddress, {
              ...existingProfile,
              connected: true,
              lastAuthenticated: new Date().toISOString()
            });
          }

          notify.success('Profile verified');
          // Force a page reload to ensure the AuthContext picks up the changes
          goToChat();
          return;
        }
      }

      // If no valid profile, create a subscription
      console.log('No valid profile found, creating subscription...');
      const info = await getWalletInfo(walletAddress);
      console.log('Wallet info retrieved:', info);

      // Create a payment transaction for subscription
      console.log('Creating payment transaction with zero amount...');
      const txnBytes = await makePaymentTransaction(
        0, // Zero amount for subscription
        walletAddress,
        algodClient,
        transactionSigner
      );

      if (!txnBytes) {
        throw new Error('Failed to create subscription payment');
      }

      console.log('Transaction created successfully, getting parameters...');

      // Get transaction parameters
      const params = await algodClient.getTransactionParams().do();

      console.log('Checking subscription with backend...');

      // Check subscription with the backend
      const result = await checkSubscription(
        walletAddress,
        txnBytes,
        params,
        info.rekeyTo // This is string | null from getWalletInfo
      );

      if (!result) {
        throw new Error('Subscription required to use this application');
      }

      console.log('Subscription verified successfully:', result);

      // Store wallet address and type in localStorage
      localStorage.setItem('walletAddress', walletAddress);
      if (activeWallet) {
        localStorage.setItem('walletType', activeWallet);
      }

      // Force a page reload to ensure the AuthContext picks up the changes
      console.log('Redirecting to chat page...');
      goToChat();

      notify.success('Authentication successful');
    } catch (error: any) {
      console.error('Authentication error:', error);
      notify.error(error?.message || 'Failed to authenticate wallet');
    } finally {
      setAuthenticating(false);
    }
  }, [authenticating, activeAccount, algodClient, activeWallet, transactionSigner]);

  const disconnectWallet = useCallback(async () => {
    try {
      if (activeAddress) {
        await clearWalletInfo(activeAddress);
      }

      // Disconnect all wallets
      for (const wallet of wallets) {
        if (wallet.isConnected) {
          await wallet.disconnect();
        }
      }

      setBalance(undefined);
      setMinBalance(undefined);
      notify.success('Wallet disconnected');
    } catch (error) {
      console.error('Disconnect error:', error);
      notify.error('Failed to disconnect wallet');
    }
  }, [activeAddress, wallets]);

  // Get the available wallets with their metadata
  const availableWallets = useMemo(() => {
    return wallets.map(wallet => {
      // Create a wallet object with metadata
      return {
        id: wallet.id,
        name: wallet.metadata?.name || wallet.id,
        metadata: {
          name: wallet.metadata?.name || wallet.id,
          icon: wallet.metadata?.icon || '',
          // Use a custom description
          description: `Algorand ${wallet.id} wallet`
        } as ExtendedWalletMetadata,
        connect: () => wallet.connect(),
        disconnect: () => wallet.disconnect()
      };
    });
  }, [wallets]);

  return {
    connectWallet: handleWalletConnect,
    authenticateWallet,
    disconnectWallet,
    connecting,
    authenticating,
    activeWallet,
    accounts,
    balance,
    minBalance,
    availableWallets
  };
};
